// src/pages/Menu/Finance/ConceptsManager.jsx
import React, { useEffect, useState } from 'react';
import { Plus, Save, X, Trash2 } from 'lucide-react';
import Swal from 'sweetalert2';
import { listIncomeConcepts, upsertIncomeConcept } from '../../../services/financeIncomesService';

export default function ConceptsManager({ open, onClose, rut }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const data = await listIncomeConcepts(rut);
      setRows(data || []);
    } catch (e) {
      Swal.fire('Error', e.message || 'No se pudieron cargar conceptos', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const add = () => setRows(prev => [...prev, { id: null, code: '', name: '', active: true, _new: true }]);
  const setRow = (i, patch) => setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const saveRow = async (i) => {
    const r = rows[i];
    if (!r?.code?.trim() || !r?.name?.trim())
      return Swal.fire({
        icon: 'warning',
        title: 'Validación',
        text: 'Código y nombre son requeridos',
        confirmButtonText: 'OK'
      });
    try {
      await upsertIncomeConcept({ rut, code: r.code.trim().toUpperCase(), name: r.name.trim(), active: !!r.active });
      Swal.fire({
        icon: 'success',
        title: '¡Registrado!',
        text: r._new ? 'Concepto creado exitosamente' : 'Concepto actualizado exitosamente',
        confirmButtonText: 'OK',
        timer: 2000
      });
      load();
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e.message || 'No se pudo guardar el concepto',
        confirmButtonText: 'OK'
      });
    }
  };

  const deleteRow = async (i) => {
    const r = rows[i];
    if (!r.id) {
      // Si es nuevo, solo lo removemos del estado
      setRows(prev => prev.filter((_, idx) => idx !== i));
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar concepto?',
      text: `Se eliminará el concepto "${r.name}". Esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280'
    });

    if (!result.isConfirmed) return;

    try {
      // Desactivar el concepto (no hay endpoint de eliminación, así que lo desactivamos)
      await upsertIncomeConcept({ rut, code: r.code, name: r.name, active: false });
      Swal.fire({
        icon: 'success',
        title: '¡Eliminado!',
        text: 'Concepto eliminado exitosamente',
        confirmButtonText: 'OK',
        timer: 2000
      });
      load();
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e.message || 'No se pudo eliminar el concepto',
        confirmButtonText: 'OK'
      });
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-gray-200 bg-gradient-to-r from-[#09274A] to-[#0488AF]">
          <div>
            <h2 className="text-xl font-bold text-white">Conceptos de Ingreso</h2>
            <p className="text-sm text-indigo-100 mt-1">Gestiona los conceptos disponibles para registrar ingresos</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-700">
              Total: <span className="font-semibold text-[#0488AF]">{rows.filter(r => r.id).length}</span> concepto{rows.filter(r => r.id).length !== 1 ? 's' : ''} registrado{rows.filter(r => r.id).length !== 1 ? 's' : ''}
            </div>
            <button 
              onClick={add} 
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-[#0488AF] text-white hover:bg-[#037a98] transition-all duration-200 shadow-md"
            >
              <Plus size={16}/> Agregar concepto
            </button>
          </div>

          <div className="border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-12 font-semibold bg-gradient-to-r from-[#0488AF]/10 to-[#09274A]/10 border-b-2 border-[#0488AF]/20 px-4 py-3 text-gray-800 text-sm">
              <div className="col-span-1 text-center">Nº</div>
              <div className="col-span-2 text-center">Código</div>
              <div className="col-span-5 text-center">Concepto</div>
              <div className="col-span-1 text-center">Activo</div>
              <div className="col-span-3 text-center">Acción</div>
            </div>
            {loading ? (
              <div className="p-8 text-sm text-gray-600 text-center">Cargando conceptos...</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-sm text-gray-500 text-center">
                No hay conceptos. Haz clic en "Agregar concepto" para crear uno nuevo.
              </div>
            ) : (
              rows.map((r, idx) => (
                <div key={r.id ?? `new-${idx}`} className={`grid grid-cols-12 items-center px-4 py-2 border-b border-gray-100 hover:bg-[#0488AF]/5 transition-colors gap-2 ${r._new ? 'bg-[#0488AF]/10' : ''}`}>
                  <div className="col-span-1 text-center text-gray-600 font-mono text-xs font-semibold">
                    {r.id || 'Nuevo'}
                  </div>
                  <div className="col-span-2">
                    <input
                      className="w-full rounded-lg border-2 border-gray-200 px-2 py-1.5 text-xs font-mono uppercase focus:border-[#0488AF] focus:ring-2 focus:ring-[#0488AF]/20 outline-none transition-all"
                      value={r.code || ''}
                      onChange={(e) => setRow(idx, { code: e.target.value })}
                      placeholder="CUOTAS_SOCIALES"
                      readOnly={!r._new && !!r.id}
                      title={!r._new && r.id ? 'El código no se puede modificar después de crear' : ''}
                    />
                  </div>
                  <div className="col-span-5">
                    <input
                      className="w-full rounded-lg border-2 border-gray-200 px-2 py-1.5 text-xs focus:border-[#0488AF] focus:ring-2 focus:ring-[#0488AF]/20 outline-none transition-all"
                      value={r.name || ''}
                      onChange={(e) => setRow(idx, { name: e.target.value })}
                      placeholder="Cuotas Sociales, Proyecto, Aporte PDT, etc."
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <input
                      type="checkbox"
                      checked={!!r.active}
                      onChange={(e) => setRow(idx, { active: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-2 border-gray-300 text-[#0488AF] focus:ring-2 focus:ring-[#0488AF]/20 cursor-pointer"
                    />
                  </div>
                  <div className="col-span-3 flex items-center justify-center gap-1.5">
                    <button
                      onClick={() => saveRow(idx)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[#0488AF] text-white hover:bg-[#037a98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm text-xs"
                      disabled={!r.code?.trim() || !r.name?.trim()}
                    >
                      <Save size={12}/> {r._new ? 'Crear' : 'Guardar'}
                    </button>
                    {r.id && (
                      <button
                        onClick={() => deleteRow(idx)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-all duration-200 shadow-sm text-xs"
                        title="Eliminar concepto"
                      >
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {!loading && rows.length > 0 && (
            <div className="mt-4 p-3 bg-gradient-to-r from-[#0488AF]/10 to-[#09274A]/10 rounded-xl border-2 border-[#0488AF]/20">
              <p className="text-sm text-gray-700">
                <strong className="text-[#0488AF]">Tip:</strong> Los conceptos se pueden usar en múltiples registros de ingresos. 
                El código debe ser único por RUT y se guarda en mayúsculas.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
